import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

interface FieldBase {
  label: ReactNode;
  /** Error text; sets aria-invalid and is announced via aria-describedby. */
  error?: string | undefined;
  hint?: ReactNode;
  /** Visually marks the field required (validation stays with the form). */
  required?: boolean;
  className?: string;
}

function useFieldIds(id: string | undefined) {
  const auto = useId();
  const fieldId = id ?? `f${auto.replace(/:/g, '')}`;
  return { fieldId, hintId: `${fieldId}-hint`, errorId: `${fieldId}-error` };
}

function describedBy(hint: ReactNode, error: string | undefined, ids: { hintId: string; errorId: string }, extra?: string): string | undefined {
  const parts = [extra, hint ? ids.hintId : undefined, error ? ids.errorId : undefined].filter(Boolean);
  return parts.length ? parts.join(' ') : undefined;
}

function Label({ htmlFor, label, required }: { htmlFor: string; label: ReactNode; required?: boolean | undefined }) {
  return (
    <label htmlFor={htmlFor}>
      {label}
      {required && (
        <span className="required" aria-hidden="true">
          *
        </span>
      )}
    </label>
  );
}

function Messages({ hint, error, hintId, errorId }: { hint: ReactNode; error: string | undefined; hintId: string; errorId: string }) {
  return (
    <>
      {hint && (
        <span id={hintId} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="field-error">
          {error}
        </span>
      )}
    </>
  );
}

export type TextFieldProps = FieldBase & Omit<InputHTMLAttributes<HTMLInputElement>, 'required'>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, required, className, id, 'aria-describedby': extraDescribedBy, ...rest },
  ref,
) {
  const ids = useFieldIds(id);
  return (
    <div className={`field ${className ?? ''}`}>
      <Label htmlFor={ids.fieldId} label={label} required={required} />
      <input
        ref={ref}
        id={ids.fieldId}
        className="input"
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, ids, extraDescribedBy)}
        {...rest}
      />
      <Messages hint={hint} error={error} {...ids} />
    </div>
  );
});

export type TextAreaProps = FieldBase & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'required'>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea({ label, error, hint, required, className, id, ...rest }, ref) {
  const ids = useFieldIds(id);
  return (
    <div className={`field ${className ?? ''}`}>
      <Label htmlFor={ids.fieldId} label={label} required={required} />
      <textarea
        ref={ref}
        id={ids.fieldId}
        className="textarea"
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, ids)}
        {...rest}
      />
      <Messages hint={hint} error={error} {...ids} />
    </div>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectProps = FieldBase &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'required'> & {
    options: readonly SelectOption[];
    /** Adds a first empty option with this text. */
    placeholder?: string;
  };

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ label, error, hint, required, className, id, options, placeholder, ...rest }, ref) {
  const ids = useFieldIds(id);
  return (
    <div className={`field ${className ?? ''}`}>
      <Label htmlFor={ids.fieldId} label={label} required={required} />
      <select
        ref={ref}
        id={ids.fieldId}
        className="select"
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(hint, error, ids)}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <Messages hint={hint} error={error} {...ids} />
    </div>
  );
});

export type CheckboxProps = Omit<FieldBase, 'required'> & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({ label, error, hint, className, id, ...rest }, ref) {
  const ids = useFieldIds(id);
  return (
    <div className={`field ${className ?? ''}`}>
      <div className="checkbox">
        <input ref={ref} type="checkbox" id={ids.fieldId} aria-invalid={error ? true : undefined} aria-describedby={describedBy(hint, error, ids)} {...rest} />
        <label htmlFor={ids.fieldId}>{label}</label>
      </div>
      <Messages hint={hint} error={error} {...ids} />
    </div>
  );
});
